import React, { useState, useEffect } from "react";
import { apiFetch } from "../services/api";
import { createDevConsole } from "../utils/devLogger";

const console = createDevConsole();

const SuperUserManagement = ({ user }) => {
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removingAdmin, setRemovingAdmin] = useState(null); // Track which admin is being removed
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchEmail, setSearchEmail] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterSuperUser, setFilterSuperUser] = useState("all");
  const [orphanedData, setOrphanedData] = useState(null);
  const [loadingOrphanedData, setLoadingOrphanedData] = useState(false);
  const [cleaningOrphanedData, setCleaningOrphanedData] = useState(false);

  useEffect(() => {
    fetchAllUsers();
    fetchOrphanedData();
  }, []);

  const fetchAllUsers = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError("");
      const response = await apiFetch("/api/admin/all-users");
      if (response.ok) {
        const data = await response.json();
        console.log(
          `📥 [Fetch Users] Received ${data.length} users from server`
        );
        setAllUsers(data);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Failed to fetch users");
      }
    } catch (err) {
      setError("Network error: " + err.message);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleSetSuperUser = async (email, isSuperUser) => {
    try {
      setError("");
      setSuccess("");
      const response = await apiFetch("/api/auth/set-super-user", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email,
          is_super_user: isSuperUser,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(
          `Super user status ${isSuperUser ? "granted" : "revoked"} for ${data.email}`
        );
        // Refresh user list
        await fetchAllUsers();
        // Refresh current user data if it's the current user
        if (email === user.email) {
          const userResponse = await apiFetch("/api/auth/me");
          if (userResponse.ok) {
            const userData = await userResponse.json();
            localStorage.setItem("user", JSON.stringify(userData));
            window.location.reload(); // Reload to update UI
          }
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Failed to update super user status");
      }
    } catch (err) {
      setError("Network error: " + err.message);
    }
  };

  const fetchOrphanedData = async () => {
    try {
      setLoadingOrphanedData(true);
      setError("");
      const response = await apiFetch("/api/admin/orphaned-data");
      if (response.ok) {
        const data = await response.json();
        setOrphanedData(data);
      } else {
        const errorData = await response.json();
        console.error("Failed to fetch orphaned data:", errorData);
      }
    } catch (err) {
      console.error("Error fetching orphaned data:", err);
    } finally {
      setLoadingOrphanedData(false);
    }
  };

  const handleCleanupOrphanedData = async (dryRun = false) => {
    const confirmMessage = dryRun
      ? "This will analyze orphaned data without deleting anything. Continue?"
      : `Are you sure you want to cleanup ALL orphaned data?\n\nThis will permanently delete:\n- Orphaned entities\n- Orphaned relationships\n- Orphaned job queue entries\n- Orphaned entity profile vectors\n\nThis action cannot be undone!`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setError("");
      setSuccess("");
      setCleaningOrphanedData(true);

      const response = await apiFetch("/api/admin/cleanup-orphaned-data", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          dry_run: dryRun,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (dryRun) {
          setSuccess(
            `DRY RUN: Would delete ${data.total} orphaned records:\n` +
              `- ${data.results.entities || 0} entities\n` +
              `- ${data.results.relationships || 0} relationships\n` +
              `- ${data.results.job_queue || 0} job queue entries\n` +
              `- ${data.results.entity_profile_vectors || 0} entity profile vectors`
          );
        } else {
          setSuccess(
            `✅ Cleaned up ${data.total} orphaned records:\n` +
              `- ${data.results.entities || 0} entities\n` +
              `- ${data.results.relationships || 0} relationships\n` +
              `- ${data.results.job_queue || 0} job queue entries\n` +
              `- ${data.results.entity_profile_vectors || 0} entity profile vectors`
          );
          // Refresh orphaned data stats
          await fetchOrphanedData();
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Failed to cleanup orphaned data");
      }
    } catch (err) {
      setError("Network error: " + err.message);
    } finally {
      setCleaningOrphanedData(false);
    }
  };

  const handleRemoveAdministrator = async (adminUser) => {
    const confirmMessage = `Are you sure you want to remove administrator "${adminUser.name}" (${adminUser.email})?\n\nThis will:\n- Delete the user account\n- Clean up all tenant data for their organization\n- Delete the organization\n\nThis action cannot be undone!`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setError("");
      setSuccess("");
      setRemovingAdmin(adminUser.id); // Set loading state for this specific admin

      console.log(
        "🗑️ [Remove Admin] Starting removal for:",
        adminUser.email,
        adminUser.id
      );

      const response = await apiFetch("/api/admin/remove-administrator", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: adminUser.id,
          email: adminUser.email,
        }),
      });

      console.log(
        "📥 [Remove Admin] Response status:",
        response.status,
        response.ok
      );

      if (response.ok) {
        const data = await response.json();
        console.log("✅ [Remove Admin] Success response:", data);

        setRemovingAdmin(null);

        // Show success message
        if (data.already_removed) {
          setSuccess(
            `ℹ️ Administrator ${adminUser.email} and their organization data were already removed.`
          );
          // Still refresh the list if we got updated_users
          if (data.updated_users) {
            console.log(
              "✅ [Remove Admin] Using updated user list from response"
            );
            setAllUsers(data.updated_users);
          } else {
            // Fallback: refresh from server
            fetchAllUsers(false).catch((err) => {
              console.error("Error refreshing user list:", err);
            });
          }
        } else {
          setSuccess(
            `✅ Administrator ${adminUser.email} and their organization data have been removed successfully.`
          );

          // Use the updated user list returned from the deletion endpoint
          // This avoids connection pool visibility issues entirely
          if (data.updated_users && Array.isArray(data.updated_users)) {
            console.log(
              `✅ [Remove Admin] Using updated user list from response: ${data.updated_users.length} users`
            );
            setAllUsers(data.updated_users);

            // Verify the deleted user is not in the list
            const userStillExists = data.updated_users.some(
              (u) => u.id === adminUser.id
            );
            if (userStillExists) {
              console.warning(
                "⚠️ [Remove Admin] Deleted user still in updated list - this should not happen!"
              );
              // Fallback: refresh from server after a delay
              setTimeout(() => {
                fetchAllUsers(false).catch((err) => {
                  console.error("Error refreshing user list:", err);
                });
              }, 1000);
            }
          } else {
            // Fallback: refresh from server if updated_users not provided
            console.log(
              "⚠️ [Remove Admin] No updated_users in response, refreshing from server"
            );
            fetchAllUsers(false).catch((err) => {
              console.error("Error refreshing user list:", err);
            });
          }
        }

        // Clear success message after a delay
        setTimeout(() => {
          setSuccess("");
        }, 3000);
      } else {
        const errorData = await response
          .json()
          .catch(() => ({ detail: "Unknown error" }));
        console.error(
          "❌ [Remove Admin] Error response:",
          response.status,
          errorData
        );

        // Check if it's a 404 - might mean already deleted
        if (response.status === 404) {
          // Optimistically remove from UI if 404 (likely already deleted)
          setAllUsers((prevUsers) =>
            prevUsers.filter((u) => u.id !== adminUser.id)
          );
          setSuccess(
            `ℹ️ Administrator ${adminUser.email} appears to have already been removed.`
          );
          // Refresh list in background
          fetchAllUsers(false).catch((err) => {
            console.error("Error refreshing user list:", err);
          });
        } else {
          setError(errorData.detail || "Failed to remove administrator");
        }
        setRemovingAdmin(null);
      }
    } catch (err) {
      console.error("❌ [Remove Admin] Exception:", err);
      setError("Network error: " + err.message);
      setRemovingAdmin(null);
    }
  };

  const filteredUsers = allUsers.filter((u) => {
    const matchesEmail =
      !searchEmail ||
      u.email.toLowerCase().includes(searchEmail.toLowerCase()) ||
      u.name.toLowerCase().includes(searchEmail.toLowerCase());
    const matchesRole = filterRole === "all" || u.role === filterRole;
    const matchesSuperUser =
      filterSuperUser === "all" ||
      (filterSuperUser === "yes" && u.is_super_user) ||
      (filterSuperUser === "no" && !u.is_super_user);
    return matchesEmail && matchesRole && matchesSuperUser;
  });

  const administrators = filteredUsers.filter(
    (u) => u.role === "Administrator"
  );

  if (loading) {
    return (
      <div className="p-4">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          <span className="ml-3 text-gray-300">Loading users...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-2">
          ⭐ Super User Management
        </h1>
        <p className="text-gray-400 text-sm">
          Manage super user status and remove administrators with tenant data
          cleanup
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-900/50 border border-red-600 rounded-md text-red-200">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-900/50 border border-green-600 rounded-md text-green-200 flex items-center">
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          {success}
        </div>
      )}

      {/* Loading Overlay for Remove Operation */}
      {removingAdmin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-700">
            <div className="flex items-center mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500 mr-4"></div>
              <div>
                <h3 className="text-white font-semibold text-lg">
                  Removing Administrator
                </h3>
                <p className="text-gray-400 text-sm mt-1">
                  This may take a few moments...
                </p>
              </div>
            </div>
            <div className="space-y-2 text-sm text-gray-300">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-red-500 rounded-full mr-2 animate-pulse"></div>
                Deleting user account...
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-red-500 rounded-full mr-2 animate-pulse"></div>
                Cleaning up tenant data...
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-red-500 rounded-full mr-2 animate-pulse"></div>
                Removing organization...
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Search
          </label>
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Role
          </label>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Roles</option>
            <option value="Administrator">Administrator</option>
            <option value="Editor">Editor</option>
            <option value="Viewer">Viewer</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Super User
          </label>
          <select
            value={filterSuperUser}
            onChange={(e) => setFilterSuperUser(e.target.value)}
            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All</option>
            <option value="yes">Super Users Only</option>
            <option value="no">Non-Super Users</option>
          </select>
        </div>
      </div>

      {/* Statistics */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
          <div className="text-gray-400 text-sm">Total Users</div>
          <div className="text-2xl font-bold text-white">{allUsers.length}</div>
        </div>
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
          <div className="text-gray-400 text-sm">Super Users</div>
          <div className="text-2xl font-bold text-yellow-400">
            {allUsers.filter((u) => u.is_super_user).length}
          </div>
        </div>
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
          <div className="text-gray-400 text-sm">Administrators</div>
          <div className="text-2xl font-bold text-blue-400">
            {allUsers.filter((u) => u.role === "Administrator").length}
          </div>
        </div>
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
          <div className="text-gray-400 text-sm">Filtered Results</div>
          <div className="text-2xl font-bold text-white">
            {filteredUsers.length}
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Super User
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    className="px-4 py-8 text-center text-gray-400"
                  >
                    No users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-700/50">
                    <td className="px-4 py-3 text-sm text-white">{u.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-300">
                      {u.email}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">
                      {u.role}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">
                      {u.organization_name || "N/A"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {u.is_super_user ? (
                        <span className="px-2 py-1 bg-yellow-600 text-white text-xs font-semibold rounded-full">
                          ⭐ Super User
                        </span>
                      ) : (
                        <span className="text-gray-400">No</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center space-x-2">
                        {!u.is_super_user ? (
                          <button
                            onClick={() => handleSetSuperUser(u.email, true)}
                            className="px-3 py-1 bg-yellow-600 text-white text-xs rounded-md hover:bg-yellow-700"
                            title="Grant super user status"
                          >
                            Grant Super User
                          </button>
                        ) : (
                          <button
                            onClick={() => handleSetSuperUser(u.email, false)}
                            className="px-3 py-1 bg-gray-600 text-white text-xs rounded-md hover:bg-gray-700"
                            title="Revoke super user status"
                          >
                            Revoke Super User
                          </button>
                        )}
                        {u.role === "Administrator" && !u.is_super_user && (
                          <button
                            onClick={() => handleRemoveAdministrator(u)}
                            disabled={removingAdmin === u.id}
                            className={`px-3 py-1 text-white text-xs rounded-md flex items-center ${
                              removingAdmin === u.id
                                ? "bg-gray-600 cursor-not-allowed"
                                : "bg-red-600 hover:bg-red-700"
                            }`}
                            title="Remove administrator and cleanup tenant data"
                          >
                            {removingAdmin === u.id ? (
                              <span className="flex items-center">
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                                Removing...
                              </span>
                            ) : (
                              "Remove Admin"
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Orphaned Data Cleanup Section */}
      <div className="mt-8 mb-8">
        <h2 className="text-xl font-bold text-white mb-4">
          🧹 Orphaned Data Cleanup
        </h2>
        <div className="bg-yellow-900/20 border border-yellow-600 rounded-lg p-4">
          <p className="text-yellow-200 text-sm mb-4">
            Orphaned data refers to records (entities, relationships, etc.) that
            have a tenant_id that doesn't match any existing organization. This
            can happen when organizations are deleted but their data wasn't
            fully cleaned up.
          </p>

          {loadingOrphanedData ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-500 mr-3"></div>
              <span className="text-gray-300">
                Loading orphaned data statistics...
              </span>
            </div>
          ) : orphanedData ? (
            <div className="space-y-4">
              {/* Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-800 p-3 rounded border border-gray-700">
                  <div className="text-gray-400 text-xs">Orphaned Entities</div>
                  <div className="text-xl font-bold text-red-400">
                    {orphanedData.statistics.entities.total}
                  </div>
                </div>
                <div className="bg-gray-800 p-3 rounded border border-gray-700">
                  <div className="text-gray-400 text-xs">
                    Orphaned Relationships
                  </div>
                  <div className="text-xl font-bold text-red-400">
                    {orphanedData.statistics.relationships.total}
                  </div>
                </div>
                <div className="bg-gray-800 p-3 rounded border border-gray-700">
                  <div className="text-gray-400 text-xs">Orphaned Jobs</div>
                  <div className="text-xl font-bold text-red-400">
                    {orphanedData.statistics.job_queue.total}
                  </div>
                </div>
              </div>

              {/* Total orphaned records */}
              <div className="bg-gray-800 p-4 rounded border border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-gray-400 text-sm">
                      Total Orphaned Records
                    </div>
                    <div className="text-3xl font-bold text-red-400 mt-1">
                      {orphanedData.statistics.entities.total +
                        orphanedData.statistics.relationships.total +
                        orphanedData.statistics.job_queue.total}
                    </div>
                    <div className="text-gray-500 text-xs mt-1">
                      Across {orphanedData.orphaned_tenant_ids.length} orphaned
                      tenant(s)
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleCleanupOrphanedData(true)}
                      disabled={cleaningOrphanedData}
                      className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed flex items-center"
                    >
                      {cleaningOrphanedData ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Analyzing...
                        </>
                      ) : (
                        "🔍 Dry Run"
                      )}
                    </button>
                    <button
                      onClick={() => handleCleanupOrphanedData(false)}
                      disabled={
                        cleaningOrphanedData ||
                        (orphanedData.statistics.entities.total === 0 &&
                          orphanedData.statistics.relationships.total === 0 &&
                          orphanedData.statistics.job_queue.total === 0)
                      }
                      className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed flex items-center"
                    >
                      {cleaningOrphanedData ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Cleaning...
                        </>
                      ) : (
                        "🗑️ Cleanup All"
                      )}
                    </button>
                    <button
                      onClick={fetchOrphanedData}
                      disabled={loadingOrphanedData}
                      className="px-4 py-2 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700 disabled:bg-gray-500 disabled:cursor-not-allowed"
                      title="Refresh statistics"
                    >
                      🔄
                    </button>
                  </div>
                </div>
              </div>

              {/* Details by tenant */}
              {orphanedData.orphaned_tenant_ids.length > 0 && (
                <div className="bg-gray-800 p-4 rounded border border-gray-700">
                  <div className="text-gray-300 text-sm font-semibold mb-2">
                    Orphaned Tenant IDs:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {orphanedData.orphaned_tenant_ids.map((tenantId) => (
                      <span
                        key={tenantId}
                        className="px-2 py-1 bg-red-900/50 text-red-200 text-xs rounded border border-red-600"
                      >
                        {tenantId}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-gray-400 text-sm">
              Failed to load orphaned data statistics. Click refresh to try
              again.
            </div>
          )}
        </div>
      </div>

      {/* Administrators Section */}
      {administrators.length > 0 && (
        <div className="mt-8">
          <h2 className="text-xl font-bold text-white mb-4">
            ⚠️ Administrators (Can be removed with data cleanup)
          </h2>
          <div className="bg-red-900/20 border border-red-600 rounded-lg p-4">
            <p className="text-red-200 text-sm mb-4">
              Removing an administrator will delete their user account, all
              tenant data for their organization, and the organization itself.
              This action cannot be undone.
            </p>
            <div className="space-y-2">
              {administrators.map((admin) => (
                <div
                  key={admin.id}
                  className="flex items-center justify-between bg-gray-800 p-3 rounded border border-gray-700"
                >
                  <div>
                    <div className="text-white font-medium">{admin.name}</div>
                    <div className="text-gray-400 text-sm">{admin.email}</div>
                    <div className="text-gray-500 text-xs">
                      Organization: {admin.organization_name || "N/A"}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveAdministrator(admin)}
                    disabled={removingAdmin === admin.id}
                    className={`px-4 py-2 text-white text-sm rounded-md flex items-center ${
                      removingAdmin === admin.id
                        ? "bg-gray-600 cursor-not-allowed"
                        : "bg-red-600 hover:bg-red-700"
                    }`}
                  >
                    {removingAdmin === admin.id ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Removing & Cleaning Up...
                      </>
                    ) : (
                      "Remove & Cleanup"
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperUserManagement;
